import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, of } from 'rxjs';
import {
  ITimeActivitySnapshot,
  ITimeAllocation,
  ITimePausePeriod,
  ITimeScreenshotCapture,
  ITimeSession,
  ITimeSheet,
  ITimeTrackingSettings,
  TimeAllocationSource,
  TimeAllocationType,
  TimeSessionSource,
  TimeSessionStatus
} from '@platfio/interfaces/platfio';
import { NativeHostService } from './native-host.service';

export interface TimeTrackingTarget {
  allocationType: TimeAllocationType;
  businessId: string;
  businessName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  taskId?: string | null;
  taskName?: string | null;
  ticketId?: string | null;
  ticketName?: string | null;
  billable: boolean;
  description: string;
}

export interface TimeTrackingActiveSession {
  userId: string;
  agencyId: string;
  dateId: string;
  sessionId: string;
  allocationId?: string | null;
  source: TimeSessionSource;
}

export interface TimeTrackingActiveIndicator {
  activeSession: TimeTrackingActiveSession | null;
  status: 'idle' | 'running' | 'paused';
}

export interface TimeTrackingDayState {
  sheet: ITimeSheet | null;
  sessions: ITimeSession[];
  allocations: ITimeAllocation[];
}

export interface TimeTrackingWeekSummary {
  sheets: ITimeSheet[];
  totalSeconds: number;
  billableSeconds: number;
  manualSeconds: number;
  webTimerSeconds: number;
  desktopTimerSeconds: number;
}

export interface TimeTrackingBusinessSearchResult {
  id: string;
  name: string;
}

export const DEFAULT_TIME_TRACKING_SETTINGS: ITimeTrackingSettings = {
  screenshotsEnabled: false,
  screenshotIntervalMinutes: 10,
  activityTrackingEnabled: true,
  idleThresholdSeconds: 300,
  allowManualTimesheets: true,
  allowTimerStartEndTimeChanges: true
};

const ACTIVE_SESSION_STORAGE_KEY = 'platfio-active-time-session';
const MIN_SNAPSHOT_INTERVAL_MINUTES = 1;
const MAX_SNAPSHOT_INTERVAL_MINUTES = 60;

@Injectable({
  providedIn: 'root'
})
export class TimeTrackingService {
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private snapshotTimer: ReturnType<typeof setInterval> | null = null;
  private activeSession: TimeTrackingActiveSession | null = null;
  private activeIndicatorSubject = new BehaviorSubject<TimeTrackingActiveIndicator>({
    activeSession: null,
    status: 'idle'
  });
  private isBrowser: boolean;
  readonly activeIndicator$ = this.activeIndicatorSubject.asObservable();

  constructor(
    private afs: AngularFirestore,
    private afStorage: AngularFireStorage,
    private nativeHostService: NativeHostService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  observeSettings(agencyId: string): Observable<ITimeTrackingSettings> {
    if (!agencyId) {
      return of({ ...DEFAULT_TIME_TRACKING_SETTINGS });
    }

    return this.afs
      .doc<Partial<ITimeTrackingSettings>>(`users/${agencyId}/timeTrackingSettings/settings`)
      .valueChanges()
      .pipe(map(settings => normalizeSettings(settings)));
  }

  async saveSettings(agencyId: string, userId: string, settings: ITimeTrackingSettings): Promise<void> {
    const normalized = normalizeSettings(settings);
    await this.afs.doc(`users/${agencyId}/timeTrackingSettings/settings`).set({
      ...normalized,
      updatedAt: new Date(),
      updatedByUid: userId
    }, { merge: true });
  }

  async searchAgencyBusinesses(agencyId: string, query: string, limit = 10): Promise<TimeTrackingBusinessSearchResult[]> {
    const term = String(query || '').trim().toLowerCase();
    if (!agencyId || term.length < 2) {
      return [];
    }

    const requestedLimit = Math.max(1, Math.min(25, Number(limit || 10)));
    const prefixMatches = await this.searchAgencyBusinessesByPrefix(agencyId, term, requestedLimit).catch(() => []);
    if (prefixMatches.length > 0) {
      return prefixMatches;
    }

    const snapshot = await this.afs.collection('users', ref =>
      ref.where('agent', '==', agencyId).limit(250)
    ).get().toPromise();

    return (snapshot?.docs || [])
      .map(doc => {
        const business: any = doc.data() || {};
        const result = this.toBusinessSearchResult(doc.id, business);
        return {
          result,
          score: this.scoreBusinessSearchResult(business, result.name, term)
        };
      })
      .filter(item => item.score > 10)
      .sort((a, b) => b.score - a.score || a.result.name.localeCompare(b.result.name))
      .slice(0, requestedLimit)
      .map(item => item.result);
  }

  observeDay(userId: string, agencyId: string, dateId: string): Observable<TimeTrackingDayState> {
    if (!userId || !agencyId || !dateId) {
      return of({ sheet: null, sessions: [], allocations: [] });
    }

    const basePath = this.getSheetPath(userId, agencyId, dateId);
    const sheet$ = this.afs.doc<ITimeSheet>(basePath).valueChanges();
    const sessions$ = this.afs.collection<ITimeSession>(`${basePath}/timeSessions`, ref => ref.orderBy('startedAt', 'desc')).valueChanges({ idField: 'id' });
    const allocations$ = this.afs.collection<ITimeAllocation>(`${basePath}/timeAllocations`, ref => ref.orderBy('startedAt', 'desc')).valueChanges({ idField: 'id' });

    return combineLatest([sheet$, sessions$, allocations$]).pipe(
      map(([sheet, sessions, allocations]) => ({
        sheet: sheet ? normalizeSheet(sheet) : null,
        sessions: sessions.map(normalizeSession),
        allocations: allocations.map(normalizeAllocation)
      }))
    );
  }

  observeWeek(userId: string, agencyId: string, anchorDate = new Date()): Observable<TimeTrackingWeekSummary> {
    if (!userId || !agencyId) {
      return of(emptyWeekSummary());
    }

    const weekDates = getWeekDateIds(anchorDate);
    const sheetStreams = weekDates.map(dateId =>
      this.afs.doc<ITimeSheet>(this.getSheetPath(userId, agencyId, dateId)).valueChanges()
    );

    return combineLatest(sheetStreams).pipe(
      map(sheets => {
        const normalizedSheets = sheets.filter(Boolean).map(sheet => normalizeSheet(sheet as ITimeSheet));
        return summarizeWeek(normalizedSheets);
      })
    );
  }

  async startTimer(userId: string, agencyId: string, source: TimeSessionSource): Promise<TimeTrackingActiveSession> {
    this.assertRequiredContext(userId, agencyId);

    const startedAt = new Date();
    const dateId = toDateId(startedAt);
    const basePath = this.getSheetPath(userId, agencyId, dateId);
    const sessionId = this.afs.createId();
    const settings = await firstValueFrom(this.observeSettings(agencyId));
    const desktopEnvironment = this.nativeHostService.getDesktopEnvironment();

    const session: ITimeSession = {
      id: sessionId,
      source,
      status: 'running',
      agencyId,
      userId,
      date: dateId,
      startedAt,
      stoppedAt: null,
      durationSeconds: 0,
      pausedSeconds: 0,
      pausePeriods: [],
      lastSavedAt: startedAt,
      submittedAt: null,
      submittedByUid: null,
      createdAt: startedAt,
      updatedAt: startedAt,
      ...(source === 'desktop'
        ? {
            desktop: {
              deviceId: desktopEnvironment?.deviceId || null,
              platform: desktopEnvironment?.platform || null,
              appVersion: desktopEnvironment?.appVersion || null,
              screenshotsEnabled: settings.screenshotsEnabled,
              activityTrackingEnabled: settings.activityTrackingEnabled
            }
          }
        : {})
    };

    await Promise.all([
      this.ensureTimeSheet(userId, agencyId, dateId, startedAt),
      this.afs.doc(`${basePath}/timeSessions/${sessionId}`).set(session)
    ]);

    const activeSession = { userId, agencyId, dateId, sessionId, allocationId: null, source };
    this.setActiveSession(activeSession, 'running');
    this.startLoops(activeSession, settings);
    return activeSession;
  }

  async addManualSession(userId: string, agencyId: string, startedAt: Date, stoppedAt: Date): Promise<ITimeSession> {
    this.assertRequiredContext(userId, agencyId);
    const settings = await firstValueFrom(this.observeSettings(agencyId));
    if (!settings.allowManualTimesheets) {
      throw new Error('Manual timesheets are disabled for this agency.');
    }
    if (Number.isNaN(startedAt.getTime()) || Number.isNaN(stoppedAt.getTime())) {
      throw new Error('Enter valid start and stop times.');
    }
    if (stoppedAt <= startedAt) {
      throw new Error('Stop time must be after start time.');
    }
    if (toDateId(startedAt) !== toDateId(stoppedAt)) {
      throw new Error('Manual time cannot span multiple days. Create one session per day.');
    }

    const dateId = toDateId(startedAt);
    const now = new Date();
    const basePath = this.getSheetPath(userId, agencyId, dateId);
    const sessionId = this.afs.createId();
    const durationSeconds = Math.max(0, Math.round((stoppedAt.getTime() - startedAt.getTime()) / 1000));
    const session: ITimeSession = {
      id: sessionId,
      source: 'web',
      status: 'stopped',
      agencyId,
      userId,
      date: dateId,
      startedAt,
      stoppedAt,
      durationSeconds,
      trackedStartedAt: null,
      trackedStoppedAt: null,
      trackedDurationSeconds: 0,
      timeAdjusted: false,
      adjustedAt: null,
      adjustedByUid: null,
      pausedSeconds: 0,
      pausePeriods: [],
      lastSavedAt: now,
      manualEntry: true,
      submittedAt: null,
      submittedByUid: null,
      createdAt: now,
      updatedAt: now
    };

    await Promise.all([
      this.ensureTimeSheet(userId, agencyId, dateId, now),
      this.afs.doc(`${basePath}/timeSessions/${sessionId}`).set(session)
    ]);
    await this.recalculateTimeSheet(userId, agencyId, dateId);
    return session;
  }

  async recoverActiveSession(userId: string, agencyId: string): Promise<TimeTrackingActiveSession | null> {
    if (!this.isBrowser) {
      return null;
    }

    const stored = window.localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    try {
      const activeSession = JSON.parse(stored) as TimeTrackingActiveSession;
      if (activeSession.userId !== userId || activeSession.agencyId !== agencyId) {
        return null;
      }

      const snapshot = await this.afs.doc<ITimeSession>(
        `${this.getSheetPath(userId, agencyId, activeSession.dateId)}/timeSessions/${activeSession.sessionId}`
      ).get().toPromise();
      const session = snapshot?.data();

      if (!session || session.status === 'stopped') {
        this.clearActiveSession();
        return null;
      }

      const settings = await firstValueFrom(this.observeSettings(agencyId));
      this.setActiveSession(activeSession, session.status === 'paused' ? 'paused' : 'running');
      this.startLoops(activeSession, settings);
      await this.saveTimerProgress(activeSession);
      return activeSession;
    } catch {
      this.clearActiveSession();
      return null;
    }
  }

  async pauseTimer(activeSession = this.activeSession): Promise<void> {
    if (!activeSession) {
      return;
    }

    const session = await this.getSession(activeSession);
    if (!session || session.status !== 'running') {
      return;
    }

    const now = new Date();
    const pausePeriods = [...(session.pausePeriods || []), { startedAt: now }];

    await this.updateSession(activeSession, {
      status: 'paused',
      durationSeconds: calculateSessionDurationSeconds(session.startedAt, now, pausePeriods),
      pausedSeconds: sumPausedSeconds(pausePeriods, now),
      pausePeriods,
      lastSavedAt: now,
      updatedAt: now
    });
    this.setActiveSession(activeSession, 'paused');
    await this.saveTimerProgress(activeSession);
  }

  async resumeTimer(activeSession = this.activeSession): Promise<void> {
    if (!activeSession) {
      return;
    }

    const session = await this.getSession(activeSession);
    if (!session || session.status !== 'paused') {
      return;
    }

    const now = new Date();
    const pausePeriods = closeLatestPause(session.pausePeriods || [], now);

    await this.updateSession(activeSession, {
      status: 'running',
      durationSeconds: calculateSessionDurationSeconds(session.startedAt, now, pausePeriods),
      pausedSeconds: sumPausedSeconds(pausePeriods, now),
      pausePeriods,
      lastSavedAt: now,
      updatedAt: now
    });
    this.setActiveSession(activeSession, 'running');
  }

  async stopTimer(activeSession = this.activeSession): Promise<void> {
    if (!activeSession) {
      return;
    }

    const session = await this.getSession(activeSession);
    if (!session) {
      this.clearActiveSession();
      return;
    }

    const now = new Date();
    const pausePeriods = session.status === 'paused'
      ? closeLatestPause(session.pausePeriods || [], now)
      : (session.pausePeriods || []);
    const durationSeconds = calculateSessionDurationSeconds(session.startedAt, now, pausePeriods);
    const pausedSeconds = sumPausedSeconds(pausePeriods, now);

    await this.updateSession(activeSession, {
      status: 'stopped',
      stoppedAt: now,
      durationSeconds,
      trackedStartedAt: session.trackedStartedAt || session.startedAt,
      trackedStoppedAt: session.trackedStoppedAt || now,
      trackedDurationSeconds: session.trackedDurationSeconds ?? durationSeconds,
      timeAdjusted: false,
      adjustedAt: null,
      adjustedByUid: null,
      pausedSeconds,
      pausePeriods,
      lastSavedAt: now,
      updatedAt: now
    });

    await this.recalculateTimeSheet(activeSession.userId, activeSession.agencyId, activeSession.dateId);
    this.clearActiveSession();
  }

  async saveTimerProgress(activeSession = this.activeSession): Promise<void> {
    if (!activeSession) {
      return;
    }

    const session = await this.getSession(activeSession);
    if (!session || session.status === 'stopped') {
      this.clearActiveSession();
      return;
    }

    const now = new Date();
    const durationSeconds = calculateSessionDurationSeconds(session.startedAt, now, session.pausePeriods || []);
    const pausedSeconds = sumPausedSeconds(session.pausePeriods || [], now);

    await this.updateSession(activeSession, {
      durationSeconds,
      pausedSeconds,
      lastSavedAt: now,
      updatedAt: now
    });
    this.setActiveSession(activeSession, session.status === 'paused' ? 'paused' : 'running');
  }

  async addAllocation(
    userId: string,
    agencyId: string,
    target: TimeTrackingTarget,
    startedAt: Date,
    stoppedAt: Date,
    source: TimeAllocationSource = 'manual',
    timeSessionId: string | null = null
  ): Promise<string> {
    this.assertRequiredContext(userId, agencyId);
    this.assertAllocationTarget(target);
    if (stoppedAt <= startedAt) {
      throw new Error('Stop time must be after start time.');
    }

    const dateId = toDateId(startedAt);
    const now = new Date();
    const allocationId = this.afs.createId();
    const durationSeconds = Math.max(0, Math.round((stoppedAt.getTime() - startedAt.getTime()) / 1000));
    const allocationType = normalizeAllocationType(target.allocationType);
    const isBreak = allocationType === 'break';
    const isBusiness = allocationType === 'business';
    const submittedAt = source === 'manual' && !timeSessionId ? now : null;
    const allocation: ITimeAllocation = {
      id: allocationId,
      source,
      allocationType,
      timeSessionId,
      agencyId,
      userId,
      businessId: isBusiness ? target.businessId : null,
      businessName: isBusiness ? target.businessName || null : null,
      projectId: isBusiness ? target.projectId || null : null,
      projectName: isBusiness ? target.projectName || null : null,
      taskId: isBusiness ? target.taskId || null : null,
      taskName: isBusiness ? target.taskName || null : null,
      ticketId: isBusiness ? target.ticketId || null : null,
      ticketName: isBusiness ? target.ticketName || null : null,
      startedAt,
      stoppedAt,
      durationSeconds,
      billable: isBusiness ? target.billable : false,
      description: target.description || (isBreak ? 'Break' : ''),
      date: dateId,
      submittedAt,
      submittedByUid: submittedAt ? userId : null,
      createdAt: now,
      updatedAt: now
    };

    const basePath = this.getSheetPath(userId, agencyId, dateId);
    await this.ensureTimeSheet(userId, agencyId, dateId, now);
    await this.afs.doc(`${basePath}/timeAllocations/${allocationId}`).set(allocation);
    await this.recalculateTimeSheet(userId, agencyId, dateId);
    return allocationId;
  }

  async addManualAllocation(
    userId: string,
    agencyId: string,
    target: TimeTrackingTarget,
    startedAt: Date,
    stoppedAt: Date
  ): Promise<string> {
    return this.addAllocation(userId, agencyId, target, startedAt, stoppedAt, 'manual', null);
  }

  async updateAllocation(
    userId: string,
    agencyId: string,
    dateId: string,
    allocationId: string,
    patch: Partial<ITimeAllocation>,
    recalculate = true
  ): Promise<void> {
    await this.afs.doc(`${this.getSheetPath(userId, agencyId, dateId)}/timeAllocations/${allocationId}`).set({
      ...patch,
      updatedAt: new Date()
    }, { merge: true });

    if (recalculate) {
      await this.recalculateTimeSheet(userId, agencyId, dateId);
    }
  }

  async deleteAllocation(userId: string, agencyId: string, dateId: string, allocation: ITimeAllocation): Promise<void> {
    const basePath = this.getSheetPath(userId, agencyId, dateId);
    await this.afs.doc(`${basePath}/timeAllocations/${allocation.id}`).delete();
    await this.recalculateTimeSheet(userId, agencyId, dateId);
  }

  async updateTimerSession(
    userId: string,
    agencyId: string,
    dateId: string,
    sessionId: string,
    patch: Partial<ITimeSession>
  ): Promise<void> {
    this.assertRequiredContext(userId, agencyId);
    if (!dateId || !sessionId) {
      throw new Error('Choose a timer session before editing it.');
    }
    if (patch.startedAt || patch.stoppedAt || patch.durationSeconds !== undefined || patch.timeAdjusted !== undefined) {
      const settings = await firstValueFrom(this.observeSettings(agencyId));
      if (!settings.allowTimerStartEndTimeChanges) {
        throw new Error('Timer start and end time changes are disabled for this agency.');
      }
    }

    await this.afs.doc(`${this.getSheetPath(userId, agencyId, dateId)}/timeSessions/${sessionId}`).set({
      ...patch,
      updatedAt: new Date()
    }, { merge: true });
  }

  async deleteTimerSession(userId: string, agencyId: string, dateId: string, sessionId: string): Promise<void> {
    this.assertRequiredContext(userId, agencyId);
    if (!dateId || !sessionId) {
      throw new Error('Choose a timer session before deleting it.');
    }

    const basePath = this.getSheetPath(userId, agencyId, dateId);
    const sessionRef = this.afs.firestore.doc(`${basePath}/timeSessions/${sessionId}`);
    const [allocationSnapshot, snapshotMetadata] = await Promise.all([
      this.afs.collection<ITimeAllocation>(`${basePath}/timeAllocations`, ref =>
        ref.where('timeSessionId', '==', sessionId)
      ).get().toPromise(),
      this.afs.collection<ITimeActivitySnapshot>(`${basePath}/timeSessions/${sessionId}/timeActivitySnapshots`).get().toPromise()
    ]);

    const batch = this.afs.firestore.batch();
    allocationSnapshot?.docs.forEach(doc => batch.delete(doc.ref));
    snapshotMetadata?.docs.forEach(doc => batch.delete(doc.ref));
    batch.delete(sessionRef);
    await batch.commit();

    if (this.activeSession?.sessionId === sessionId) {
      this.clearActiveSession();
    }
    await this.recalculateTimeSheet(userId, agencyId, dateId);
  }

  async submitSession(userId: string, agencyId: string, dateId: string, sessionId: string): Promise<void> {
    this.assertRequiredContext(userId, agencyId);
    if (!dateId || !sessionId) {
      throw new Error('Choose a timer session before submitting time.');
    }

    const basePath = this.getSheetPath(userId, agencyId, dateId);
    const sessionSnapshot = await this.afs.doc<ITimeSession>(`${basePath}/timeSessions/${sessionId}`).get().toPromise();
    const session = sessionSnapshot?.exists
      ? normalizeSession({ ...(sessionSnapshot.data() as ITimeSession), id: sessionSnapshot.id })
      : null;
    if (!session) {
      throw new Error('Timer session was not found.');
    }
    if (session.status !== 'stopped') {
      throw new Error('Stop the timer before submitting it.');
    }
    if (session.submittedAt) {
      return;
    }

    const allocationSnapshot = await this.afs.collection<ITimeAllocation>(`${basePath}/timeAllocations`, ref =>
      ref.where('timeSessionId', '==', sessionId)
    ).get().toPromise();
    const allocations = allocationSnapshot?.docs.map(doc => normalizeAllocation({ ...(doc.data() as ITimeAllocation), id: doc.id })) || [];
    const allocatedSeconds = sumDurations(allocations);
    const requiredSeconds = Math.max(0, Number(session.durationSeconds || 0));
    if (!allocations.length || allocatedSeconds < requiredSeconds - 59) {
      throw new Error('Allocate the full session before submitting it.');
    }

    const now = new Date();
    const batch = this.afs.firestore.batch();
    batch.set(this.afs.firestore.doc(`${basePath}/timeSessions/${sessionId}`), {
      submittedAt: now,
      submittedByUid: userId,
      updatedAt: now
    }, { merge: true });
    allocationSnapshot?.docs.forEach(doc => {
      batch.set(doc.ref, {
        submittedAt: now,
        submittedByUid: userId,
        updatedAt: now
      }, { merge: true });
    });
    await batch.commit();
    await this.recalculateTimeSheet(userId, agencyId, dateId);
  }

  async captureDesktopSnapshot(activeSession = this.activeSession): Promise<void> {
    if (!activeSession || activeSession.source !== 'desktop' || !this.nativeHostService.isElectronDesktop()) {
      return;
    }

    const settings = await firstValueFrom(this.observeSettings(activeSession.agencyId));
    const activityResult = settings.activityTrackingEnabled
      ? await this.nativeHostService.captureTimeActivity()
      : null;
    const screenshotResult = settings.screenshotsEnabled
      ? await this.nativeHostService.captureScreenshot()
      : null;

    const now = new Date();
    const snapshotId = this.afs.createId();
    let screenshotPath: string | null = null;
    let screenshotThumbPath: string | null = null;
    let screenshotCaptures: ITimeScreenshotCapture[] | null = null;

    if (settings.screenshotsEnabled && screenshotResult?.ok && screenshotResult.dataUrl) {
      const screenshots = screenshotResult.screenshots?.length
        ? screenshotResult.screenshots
        : [{ dataUrl: screenshotResult.dataUrl, name: 'Display 1', sourceId: null, displayId: null }];

      screenshotCaptures = await Promise.all(screenshots.map(async (screen, index): Promise<ITimeScreenshotCapture> => {
        const fileKey = this.getScreenshotFileKey(screen.name, index);
        const path = this.getScreenshotPath(activeSession, snapshotId, `${fileKey}.png`);
        const thumbPath = this.getScreenshotPath(activeSession, snapshotId, `${fileKey}-thumb.png`);
        await Promise.all([
          this.afStorage.ref(path).putString(screen.dataUrl, 'data_url', { contentType: 'image/png' }),
          this.afStorage.ref(thumbPath).putString(screen.dataUrl, 'data_url', { contentType: 'image/png' })
        ]);

        return {
          displayId: screen.displayId || null,
          name: screen.name || `Display ${index + 1}`,
          path,
          sourceId: screen.sourceId || null,
          thumbPath
        };
      }));

      screenshotPath = screenshotCaptures[0]?.path || null;
      screenshotThumbPath = screenshotCaptures[0]?.thumbPath || null;
    }

    const keyboardActivityCount = activityResult?.ok ? activityResult.keyboardActivityCount || 0 : null;
    const mouseActivityCount = activityResult?.ok ? activityResult.mouseActivityCount || 0 : null;
    const idleSeconds = activityResult?.ok ? activityResult.idleSeconds || 0 : null;

    const snapshot: ITimeActivitySnapshot = {
      id: snapshotId,
      agencyId: activeSession.agencyId,
      userId: activeSession.userId,
      date: activeSession.dateId,
      timeSessionId: activeSession.sessionId,
      capturedAt: now,
      screenshotPath,
      screenshotThumbPath,
      screenshotCaptures,
      keyboardActivityCount,
      keypressIntervalsMs: activityResult?.ok ? activityResult.keypressIntervalsMs || [] : null,
      mouseActivityCount,
      mouseClickCount: activityResult?.ok ? activityResult.mouseClickCount || 0 : null,
      mouseClickIntervalsMs: activityResult?.ok ? activityResult.mouseClickIntervalsMs || [] : null,
      mouseMovementCount: activityResult?.ok ? activityResult.mouseMovementCount || 0 : null,
      mouseScrollCount: activityResult?.ok ? activityResult.mouseScrollCount || 0 : null,
      globalInputTrackingActive: activityResult?.ok ? activityResult.globalInputTrackingActive ?? null : null,
      globalInputTrackingError: activityResult?.ok ? activityResult.globalInputTrackingError || null : null,
      idleSeconds,
      activityScore: calculateActivityScore(keyboardActivityCount || 0, mouseActivityCount || 0, idleSeconds || 0, settings.idleThresholdSeconds),
      activeApp: activityResult?.activeApp || null,
      windowTitle: activityResult?.windowTitle || null,
      browserUrl: activityResult?.browserUrl || null,
      openWindows: activityResult?.openWindows || null,
      createdAt: now
    };

    await this.afs
      .doc(`${this.getSheetPath(activeSession.userId, activeSession.agencyId, activeSession.dateId)}/timeSessions/${activeSession.sessionId}/timeActivitySnapshots/${snapshotId}`)
      .set(snapshot);
  }

  startLoops(activeSession: TimeTrackingActiveSession, settings: ITimeTrackingSettings): void {
    this.stopLoops();
    this.activeSession = activeSession;
    this.heartbeatTimer = setInterval(() => {
      void this.saveTimerProgress(activeSession);
    }, 30000);

    if (activeSession.source === 'desktop' && (settings.screenshotsEnabled || settings.activityTrackingEnabled)) {
      const intervalMinutes = clampIntervalMinutes(settings.screenshotIntervalMinutes);
      this.snapshotTimer = setInterval(() => {
        void this.captureDesktopSnapshot(activeSession);
      }, intervalMinutes * 60 * 1000);
      void this.captureDesktopSnapshot(activeSession);
    }
  }

  stopLoops(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.snapshotTimer) {
      clearInterval(this.snapshotTimer);
      this.snapshotTimer = null;
    }
  }

  async recalculateTimeSheet(userId: string, agencyId: string, dateId: string): Promise<void> {
    const basePath = this.getSheetPath(userId, agencyId, dateId);
    const [allocationsSnapshot, sessionsSnapshot] = await Promise.all([
      this.afs.collection<ITimeAllocation>(`${basePath}/timeAllocations`).get().toPromise(),
      this.afs.collection<ITimeSession>(`${basePath}/timeSessions`).get().toPromise()
    ]);
    const allocations = allocationsSnapshot?.docs.map(doc => normalizeAllocation({ ...(doc.data() as ITimeAllocation), id: doc.id })) || [];
    const submittedAllocations = allocations.filter(allocation => !!allocation.submittedAt);
    const sessions = sessionsSnapshot?.docs.map(doc => normalizeSession({ ...(doc.data() as ITimeSession), id: doc.id })) || [];
    const now = new Date();

    const sheet: ITimeSheet = {
      id: dateId,
      userId,
      agencyId,
      date: dateId,
      totalSeconds: sumDurations(submittedAllocations),
      billableSeconds: sumDurations(submittedAllocations.filter(allocation => allocation.billable)),
      allocationCount: submittedAllocations.length,
      sessionCount: sessions.length,
      manualSeconds: sumDurations(submittedAllocations.filter(allocation => allocation.source === 'manual')),
      webTimerSeconds: sumDurations(submittedAllocations.filter(allocation => allocation.source === 'web_timer')),
      desktopTimerSeconds: sumDurations(submittedAllocations.filter(allocation => allocation.source === 'desktop_timer')),
      createdAt: now,
      updatedAt: now
    };

    await this.afs.doc(basePath).set(sheet, { merge: true });
  }

  getStoredActiveSession(): TimeTrackingActiveSession | null {
    if (!this.isBrowser) {
      return null;
    }

    const stored = window.localStorage.getItem(ACTIVE_SESSION_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  getSheetPath(userId: string, agencyId: string, dateId: string): string {
    return `users/${userId}/agencyTime/${agencyId}/timeSheets/${dateId}`;
  }

  private async ensureTimeSheet(userId: string, agencyId: string, dateId: string, now: Date): Promise<void> {
    await this.afs.doc(this.getSheetPath(userId, agencyId, dateId)).set({
      id: dateId,
      userId,
      agencyId,
      date: dateId,
      totalSeconds: 0,
      billableSeconds: 0,
      allocationCount: 0,
      sessionCount: 0,
      manualSeconds: 0,
      webTimerSeconds: 0,
      desktopTimerSeconds: 0,
      createdAt: now,
      updatedAt: now
    }, { merge: true });
  }

  private async getSession(activeSession: TimeTrackingActiveSession): Promise<ITimeSession | null> {
    const snapshot = await this.afs.doc<ITimeSession>(
      `${this.getSheetPath(activeSession.userId, activeSession.agencyId, activeSession.dateId)}/timeSessions/${activeSession.sessionId}`
    ).get().toPromise();

    return snapshot?.exists ? normalizeSession({ ...(snapshot.data() as ITimeSession), id: snapshot.id }) : null;
  }

  private async updateSession(activeSession: TimeTrackingActiveSession, patch: Partial<ITimeSession>): Promise<void> {
    await this.afs.doc(
      `${this.getSheetPath(activeSession.userId, activeSession.agencyId, activeSession.dateId)}/timeSessions/${activeSession.sessionId}`
    ).set(patch, { merge: true });
  }

  private setActiveSession(activeSession: TimeTrackingActiveSession, status: 'running' | 'paused' = 'running'): void {
    this.activeSession = activeSession;
    if (this.isBrowser) {
      window.localStorage.setItem(ACTIVE_SESSION_STORAGE_KEY, JSON.stringify(activeSession));
    }
    this.activeIndicatorSubject.next({ activeSession, status });
  }

  private clearActiveSession(): void {
    this.stopLoops();
    this.activeSession = null;
    if (this.isBrowser) {
      window.localStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
    }
    this.activeIndicatorSubject.next({ activeSession: null, status: 'idle' });
  }

  private getScreenshotPath(activeSession: TimeTrackingActiveSession, snapshotId: string, fileName: string): string {
    return `time-tracking/${activeSession.userId}/${activeSession.agencyId}/${activeSession.dateId}/${activeSession.sessionId}/${snapshotId}/${fileName}`;
  }

  private getScreenshotFileKey(name: string | null | undefined, index: number): string {
    const label = String(name || `display-${index + 1}`)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
    return `${String(index + 1).padStart(2, '0')}-${label || 'display'}`;
  }

  private assertRequiredContext(userId: string, agencyId: string): void {
    if (!userId || !agencyId) {
      throw new Error('User and agency are required for time tracking.');
    }
  }

  private assertAllocationTarget(target: TimeTrackingTarget): void {
    const allocationType = normalizeAllocationType(target.allocationType);
    if (allocationType === 'business' && !target.businessId) {
      throw new Error('Select a business before tracking time.');
    }

    if (allocationType === 'internal' && !target.description?.trim()) {
      throw new Error('Add a description for internal time.');
    }
  }

  private async searchAgencyBusinessesByPrefix(
    agencyId: string,
    term: string,
    limit: number
  ): Promise<TimeTrackingBusinessSearchResult[]> {
    const snapshot = await this.afs.collection('users', ref =>
      ref.where('agent', '==', agencyId)
        .orderBy('businessNameLower')
        .startAt(term)
        .endAt(`${term}\uf8ff`)
        .limit(limit)
    ).get().toPromise();

    return (snapshot?.docs || [])
      .map(doc => this.toBusinessSearchResult(doc.id, doc.data() || {}))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private toBusinessSearchResult(id: string, business: any): TimeTrackingBusinessSearchResult {
    const personName = `${business.firstName || ''} ${business.lastName || ''}`.trim();
    return {
      id: id || business.uid,
      name: business.businessName || personName || business.email || 'Unnamed business'
    };
  }

  private scoreBusinessSearchResult(business: any, displayName: string, query: string): number {
    const fields = [
      { value: displayName, weight: 3 },
      { value: business.businessName, weight: 3 },
      { value: `${business.firstName || ''} ${business.lastName || ''}`.trim(), weight: 2 },
      { value: business.email, weight: 1.5 },
      { value: business.phone || business.phoneNumber, weight: 1 }
    ];

    return fields.reduce((bestScore, field) => {
      return Math.max(bestScore, this.fuzzyScore(query, field.value) * field.weight);
    }, 0);
  }

  private fuzzyScore(query: string, value: any): number {
    const search = String(query || '').trim().toLowerCase();
    const target = String(value || '').trim().toLowerCase();
    if (!search || !target) {
      return 0;
    }
    if (target === search) {
      return 100;
    }
    if (target.startsWith(search)) {
      return 90;
    }
    if (target.includes(search)) {
      return 70;
    }

    const searchWords = search.split(/\s+/).filter(Boolean);
    const targetWords = target.split(/\s+/).filter(Boolean);
    if (searchWords.length > 0 && searchWords.every(word => targetWords.some(targetWord => targetWord.includes(word)))) {
      return 60;
    }

    let score = 0;
    let searchIndex = 0;
    for (const character of target) {
      if (character === search[searchIndex]) {
        score += 10;
        searchIndex++;
      }
      if (searchIndex >= search.length) {
        break;
      }
    }

    if (searchIndex === search.length) {
      score += 20;
    }

    return score * Math.min(search.length / target.length, 1);
  }
}

export function toDateId(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function calculateSessionDurationSeconds(startedAt: Date, asOf: Date, pausePeriods: ITimePausePeriod[] = []): number {
  const elapsedSeconds = Math.max(0, Math.round((asOf.getTime() - startedAt.getTime()) / 1000));
  return Math.max(0, elapsedSeconds - sumPausedSeconds(pausePeriods, asOf));
}

export function sumPausedSeconds(pausePeriods: ITimePausePeriod[] = [], asOf = new Date()): number {
  return pausePeriods.reduce((total, period) => {
    const startedAt = coerceDate(period.startedAt);
    const stoppedAt = period.stoppedAt ? coerceDate(period.stoppedAt) : asOf;
    return total + Math.max(0, Math.round((stoppedAt.getTime() - startedAt.getTime()) / 1000));
  }, 0);
}

export function closeLatestPause(pausePeriods: ITimePausePeriod[], stoppedAt: Date): ITimePausePeriod[] {
  return pausePeriods.map((period, index) => {
    const isLast = index === pausePeriods.length - 1;
    if (!isLast || period.stoppedAt) {
      return period;
    }

    return { ...period, stoppedAt };
  });
}

export function calculateActivityScore(
  keyboardActivityCount: number,
  mouseActivityCount: number,
  idleSeconds: number,
  idleThresholdSeconds = DEFAULT_TIME_TRACKING_SETTINGS.idleThresholdSeconds
): number {
  if (idleSeconds >= idleThresholdSeconds) {
    return 0;
  }

  const activityCount = keyboardActivityCount + mouseActivityCount;
  return Math.max(0, Math.min(100, Math.round(activityCount * 5)));
}

export function getWeekDateIds(anchorDate: Date): string[] {
  const start = new Date(anchorDate);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  start.setHours(0, 0, 0, 0);

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return toDateId(date);
  });
}

export function normalizeSettings(settings?: Partial<ITimeTrackingSettings> | null): ITimeTrackingSettings {
  return {
    ...DEFAULT_TIME_TRACKING_SETTINGS,
    ...(settings || {}),
    screenshotIntervalMinutes: clampIntervalMinutes(settings?.screenshotIntervalMinutes || DEFAULT_TIME_TRACKING_SETTINGS.screenshotIntervalMinutes),
    idleThresholdSeconds: Math.max(60, Number(settings?.idleThresholdSeconds || DEFAULT_TIME_TRACKING_SETTINGS.idleThresholdSeconds)),
    allowManualTimesheets: settings?.allowManualTimesheets !== false,
    allowTimerStartEndTimeChanges: settings?.allowTimerStartEndTimeChanges !== false
  };
}

function clampIntervalMinutes(value: number): number {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return DEFAULT_TIME_TRACKING_SETTINGS.screenshotIntervalMinutes;
  }
  return Math.max(MIN_SNAPSHOT_INTERVAL_MINUTES, Math.min(MAX_SNAPSHOT_INTERVAL_MINUTES, Math.round(numericValue)));
}

function normalizeSheet(sheet: ITimeSheet): ITimeSheet {
  return {
    ...sheet,
    createdAt: coerceDate(sheet.createdAt),
    updatedAt: coerceDate(sheet.updatedAt)
  };
}

function normalizeSession(session: ITimeSession): ITimeSession {
  return {
    ...session,
    startedAt: coerceDate(session.startedAt),
    stoppedAt: session.stoppedAt ? coerceDate(session.stoppedAt) : null,
    trackedStartedAt: session.trackedStartedAt ? coerceDate(session.trackedStartedAt) : null,
    trackedStoppedAt: session.trackedStoppedAt ? coerceDate(session.trackedStoppedAt) : null,
    adjustedAt: session.adjustedAt ? coerceDate(session.adjustedAt) : null,
    submittedAt: session.submittedAt ? coerceDate(session.submittedAt) : null,
    pausePeriods: (session.pausePeriods || []).map(period => ({
      startedAt: coerceDate(period.startedAt),
      stoppedAt: period.stoppedAt ? coerceDate(period.stoppedAt) : null
    })),
    lastSavedAt: coerceDate(session.lastSavedAt),
    createdAt: coerceDate(session.createdAt),
    updatedAt: coerceDate(session.updatedAt)
  };
}

function normalizeAllocation(allocation: ITimeAllocation): ITimeAllocation {
  return {
    ...allocation,
    allocationType: normalizeAllocationType(allocation.allocationType),
    startedAt: coerceDate(allocation.startedAt),
    stoppedAt: coerceDate(allocation.stoppedAt),
    submittedAt: allocation.submittedAt ? coerceDate(allocation.submittedAt) : null,
    createdAt: coerceDate(allocation.createdAt),
    updatedAt: coerceDate(allocation.updatedAt)
  };
}

function normalizeAllocationType(type: TimeAllocationType | 'work' | null | undefined): TimeAllocationType {
  if (type === 'break' || type === 'internal') {
    return type;
  }

  return 'business';
}

function coerceDate(value: any): Date {
  if (!value) {
    return new Date();
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  return new Date(value);
}

function sumDurations(allocations: ITimeAllocation[]): number {
  return allocations.reduce((total, allocation) => total + Math.max(0, Number(allocation.durationSeconds || 0)), 0);
}

function summarizeWeek(sheets: ITimeSheet[]): TimeTrackingWeekSummary {
  return {
    sheets,
    totalSeconds: sheets.reduce((total, sheet) => total + Number(sheet.totalSeconds || 0), 0),
    billableSeconds: sheets.reduce((total, sheet) => total + Number(sheet.billableSeconds || 0), 0),
    manualSeconds: sheets.reduce((total, sheet) => total + Number(sheet.manualSeconds || 0), 0),
    webTimerSeconds: sheets.reduce((total, sheet) => total + Number(sheet.webTimerSeconds || 0), 0),
    desktopTimerSeconds: sheets.reduce((total, sheet) => total + Number(sheet.desktopTimerSeconds || 0), 0)
  };
}

function emptyWeekSummary(): TimeTrackingWeekSummary {
  return {
    sheets: [],
    totalSeconds: 0,
    billableSeconds: 0,
    manualSeconds: 0,
    webTimerSeconds: 0,
    desktopTimerSeconds: 0
  };
}
